<script setup lang="ts">
import { computed, onBeforeUnmount } from 'vue';

import { useChargeManagement } from "@/store/charge-management";
import { useStation, type Coupon } from "@/store/station";
import { useUserStore } from "@/store/user";
import { useDisplay } from "vuetify";
import { useI18n } from "vue-i18n";

import Battery from "@/components/common/icons/battery-icon.vue";
import StopChargeBtn from "./stop-charge-btn.vue";


const station = useStation();
const charge_management = useChargeManagement();
const user = useUserStore();
const { name } = useDisplay();
const { t } = useI18n();

const show = computed((): boolean => {
  if (station.charging) {
    charge_management.show_overlay();
  } else {
    charge_management.hide_overlay();
  }

  return charge_management.is_overlay_visible;
});

const total_kWh = computed((): string => {
  const power: number = station.total_Wh / 1000;
  console.log(`[charge-status]: total power is ${power}`);
  return power.toFixed(2);
});

const rname = computed((): string => {
  let username = station.username || "";
  console.log(`[charge-status]: currently is charging: ${username || "???"}`);
  const as_array = [...username];
  const idx = as_array.findIndex((ch) => ch == "$" || ch == "#");
  if (idx !== -1) {
    username = as_array.splice(idx + 1).join("");
  }
  return username || "???";
});

const suspended = computed(() => station.is_suspended);

const last_power = computed((): string => {
  const power: number = station.power / 1000;
  console.log(`[charge-status]: last power is ${power}`);
  return power.toFixed(2);
});

const occupied = computed((): boolean => {
  if (!charge_management.is_overlay_visible) {
    return false;
  }
  const charge = station.active_charge;
  if (charge === null) return false;
  return charge.user.id !== user.id;
});

const total_price = computed((): string | null => {
  const coupon: Coupon | null = station.coupon;
  let base_cents = station.price; // cents/kWh
  if (base_cents === null) { return null; }
  if (coupon !== null) {
    switch (coupon.ctype) {
    case "FREE":
      base_cents = 0;
      break;
    case "PERCENT":
      base_cents *= parseFloat(coupon.metadata?.percentage_reduction ?? "100") / 100;
      break;
    case "FIXED":
      base_cents = parseInt(coupon.metadata?.price ?? `${base_cents}`);
      break;
    }
  }
  return (base_cents * (station.total_Wh / 1000) / 100).toFixed(2);
});

const show_price = computed((): boolean => {
  return user.is_guest && total_price.value !== null;
});

const quote = computed((): { author: string, text: string } => {
  const index = Math.floor(Math.random() * Object.keys(t("quotes")).length);
  return {
    author: t(`quotes[${index}][0]`),
    text: t(`quotes[${index}][1]`),
  };
});

const card_width = computed(() => {
  switch (name.value) {
    case "xs":
    case "sm":
      return "w-100";
    default:
      return "w-50";
  }
});

function charge_started() {
  if (station.test_mode && station.active_charge) {
    const now = Date.now();
    console.log(now);
    console.log(new Date(station.active_charge.start).getTime());
    console.log(now - new Date(station.active_charge.start).getTime());
    return now - (station.active_charge ? new Date(station.active_charge.start).getTime() : now) >
      9000;
  }
  return total_kWh.value !== '0.00';
}

onBeforeUnmount(() => charge_management.clear_warning_interval())
</script>

<template>
  <v-overlay
    v-model="show"
    persistent
    no-click-animation
    class="align-center justify-center"
    z-index="1500"
    style="background-color: rgba(25, 221, 38, 0.80); height: calc(100vh - 65px); margin-top: 65px; z-index: 1000;"
  >
    <!-- TODO: check if I can remove these v-col -->
    <v-sheet class="align-center justify-center" color="transparent" width="100%" fill-height fluid>
      <v-col v-if="occupied" class="align-center">
        <v-row class="justify-center">
          <h2 class="title text-white text-center text-uppercase font-weight-bold">
            {{ t("already_occupied") }} {{ rname }}
          </h2>
        </v-row>
      </v-col>
      <v-col v-else class="justify-center align-center">
        <transition name="fade" mode="out-in">
          <div v-if="charge_started()">
            <battery class="mb-2 mt-8 justify-center" />
            <v-container class="text-white" fill-height>
              <v-col class="align-center">
                <!-- Show a card with a warning if the charge is suspended -->
                <Transition name="rotate">
                  <v-row class="justify-center text-wrap text-center mb-4">
                    <v-sheet
                      v-show="suspended" rounded
                      :class="`pa-3 bg-transparent ${card_width} text-wrap text-center rounded-xl`"
                      style="border-style: dashed; border-width: 4px; border-color: black;"
                    >
                      <span class="text-body-3 justify-center">
                        {{ t("suspended") }}
                      </span>
                    </v-sheet>
                  </v-row>
                </Transition>
                <!--  Total energy consumed for User: N kWh -->
                <v-row class="justify-center align-center text-wrap">
                  <div v-if="!user.is_guest" class="text-center">
                    <span>{{ t("total") }}</span>
                    <span class="ml-1">{{ rname }}:</span>
                  </div>
                  <span v-else>{{ t("total_guest") }}:</span>
                  <br>
                </v-row>
                <v-row class="justify-center align-center text-wrap mt-4 mb-2">
                  <Transition name="rotate">
                    <div class="text-h3">{{ total_kWh }}</div>
                  </Transition>
                  <div class="text-h4 mt-1">&nbsp;kWh</div>
                </v-row>
                <!-- price, only for guests -->
                <v-col
                  v-if="show_price"
                  class="text-caption justify-center align-center"
                >
                  <v-row class="justify-center">
                    <Transition name="rotate">
                      <div class="text-h4">{{ total_price }}€</div>
                    </Transition>
                  </v-row>
                </v-col>
                <!-- Power: X kW -->
                <v-col class="text-body-2 font-italic align-center">
                  <v-row class="justify-center text-wrap">
                    <span>{{ t("last_power") }}:</span>
                    <Transition name="rotate">
                      <span class="text-body-2 font-italic">
                        &nbsp;{{ last_power }} kW
                      </span>
                    </Transition>
                  </v-row>
                </v-col>
              </v-col>
            </v-container>
          </div>
          <div v-else>
            <v-container class="align-center">
              <v-col class="align-center text-white">
                <v-row class="justify-center">
                  <v-progress-circular size="50" width="7" indeterminate />
                </v-row>
                <v-row class="justify-center">
                  <p class="text-h6 justify-center text-center pt-5 text-pre-wrap whitespace-pre">
                    {{ t('charge_is_starting') }}
                  </p>
                </v-row>
              </v-col>
            </v-container>
          </div>
        </transition>
        <stop-charge-btn />
        <!-- quote -->
        <v-container v-if="station.want_quotes" class="text-white px-10 mt-10" fill-height>
          <v-col>
            <v-row class="justify-center">
              <p class="text-body-2 text-center font-italic pb-3">
                {{ quote.text }}
              </p>
              <br>
            </v-row>
            <v-row class="text-center justify-center mt-0 px-2 pb-10 pt-3">
              <p class="text-body-2">{{ quote.author }}</p>
            </v-row>
          </v-col>
        </v-container>
        <!-- end quote -->
      </v-col>
    </v-sheet>
  </v-overlay>
</template>

<i18n>
{
  "en": {
    "quotes": [
      [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ],
      [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ],
      [
        "Philippe St Marc",
        "Man must be the guardian of nature, not its owner."
      ],
      [
        "Gandhi",
        "The world has enough for everyone’s needs, but not enough for everyone’s greed."
      ],
      [
        "Pierre Joliot-Curie",
        "A society that survives by creating artificial needs to efficiently produce useless consumer goods does not seem capable of responding in the long term to the challenges posed by the degradation of our environment."
      ],
      [
        "Albert Einstein",
        "What characterizes our era is the perfection of means and the confusion of ends."
      ],
      [
        "Philippe St Marc",
        "The cost of protecting the natural environment is much lower than the cost of its reconstruction. Defending nature is profitable for nations."
      ],
      [
        "Antoine de Saint-Exupéry",
        "We do not inherit the earth from our parents, we borrow it from our children."
      ],
      [
        "Charles F. Kettering",
        "The world hates change, yet it is the only thing that has allowed it to progress."
      ],
      [
        "Peter Drucker",
        "There is certainly nothing so useless as doing very efficiently what should not be done at all."
      ],
      [
        "Martin Luther King",
        "If I were told that the world would end tomorrow, I would still plant an apple tree."
      ],
      [
        "Kenneth Boulding",
        "Anyone who believes that infinite growth is possible in a finite world is either a madman or an economist."
      ],
      [
        "Victor Hugo",
        "It is a sad thing to think that nature speaks and that mankind does not listen."
      ],
      [
        "Marcus Aurelius",
        "Always go by the shortest route, and the shortest route is the one traced by nature."
      ],
      [
        "Confucius",
        "He who moves a mountain begins by moving small stones."
      ],
      [
        "Henry Ford",
        "Do not look for the fault, look for the remedy."
      ],
      [
        "Saint Augustine",
        "It is better to follow the right path limping than the wrong one with a firm step."
      ]
    ],
    "stop_charge": "Stop charge",
    "last_power": "Power",
    "charge_is_starting": "The chargepoint is powering on.\nIt can take up to 30 seconds",
    "already_consumed": "Consumed energy",
    "already_occupied": "Terminal occupied by",
    "total": "Energy supplied to",
    "total_guest": "Energy supplied",
    "confirm_stop_charge": "Are you sure you want stop charging?",
    "suspended": "This chargepoint is currently suspended: there is not enough power to supply all the charging stations."
  },
  "it": {
    "quotes": [
      [
        "Papa Francesco, Enciclica Laudato Si’",
        "«Il mondo è qualcosa di più che un problema da risolvere, è un mistero gaudioso che contempliamo nella letizia e nella lode.»"
      ],
      [
        "Papa Francesco, Enciclica Laudato Si’",
        "«I giovani esigono da noi un cambiamento. Essi si domandano com’è possibile che si pretenda di costruire un futuro migliore senza pensare alla crisi ambientale e alle sofferenze degli esclusi. »"
      ],
      [
        "Madre Teresa di Calcutta",
        "«Non aspettare che arrivino i leader. Fallo da solo, persona per persona.»"
      ],
      [
        "Papa Francesco, Enciclica Laudato Si’",
        "«Ciò che sta accadendo ci pone di fronte all’urgenza di procedere in una coraggiosa rivoluzione culturale.»"
      ],
      [
        "Papa Francesco",
        "«Prendersi cura dell’ambiente significa avere un atteggiamento di ecologia umana. [...] Non si può separare l’uomo dal resto; c’è una relazione che incide in maniera reciproca, sia dell’ambiente sulla persona, sia della persona nel modo in cui tratta l’ambiente.»"
      ],
      [
        "Papa Francesco, Enciclica Laudato Si’",
        "«Eppure, non tutto è perduto, perché gli esseri umani … possono anche superarsi, ritornare a scegliere il bene e rigenerarsi, al di là di qualsiasi condizionamento psicologico e sociale che venga loro imposto.»"
      ],
      [
        "John Muir",
        "«Qualsiasi stupido è capace di distruggere gli alberi; non possono né difendersi né scappare.»"
      ],
      [
        "Jacques-Yves Cousteau",
        "«Conoscere il tuo pianeta è un passo verso il proteggerlo.»"
      ],
      [
        "Papa Francesco",
        "«Vi è un chiaro legame tra la protezione della natura e l’edificazione di un ordine sociale giusto ed equo. Non vi può essere un rinnovamento del nostro rapporto con la natura senza un rinnovamento dell’umanità stessa.»"
      ],
      [
        "John Muir",
        "«Quando noi cerchiamo di staccare qualcosa da se stessa, scopriamo che è collegata a qualsiasi altra cosa nell’universo.»"
      ],
      [
        "Greta Thunberg",
        "«Non sei mai troppo piccolo per fare la differenza.»"
      ],
      [
        "Thomas Edison",
        "«Il valore di un'idea sta nel metterla in pratica.»"
      ],
      [
        "Fabrice Hadjadj",
        "«L'uomo prima è meravigliato, poi si muove.»"
      ],
      [
        "Fabrice Hadjadj",
        "«La vera tradizione non consiste nel conservare tutto di ciò che si faceva ieri, ma nel trasmetterne l'essenziale.»"
      ],
      [
        "Philippe St Marc",
        "L’uomo deve essere il custode della natura, non il suo proprietario."
      ],
      [
        "Gandhi",
        "Il mondo contiene abbastanza per i bisogni di ognuno, ma non abbastanza per l'avidità di tutti."
      ],
      [
        "Pierre Joliot-Curie",
        "Una società che sopravvive creando bisogni artificiali per produrre efficacemente beni di consumo inutili non sembra in grado di rispondere a lungo termine alle sfide poste dal degrado del nostro ambiente."
      ],
      [
        "Albert Einstein",
        "Ciò che caratterizza la nostra epoca è la perfezione dei mezzi e la confusione dei fini."
      ],
      [
        "Philippe St Marc",
        "Il costo della protezione dell’ambiente naturale è molto più basso del costo della sua ricostituzione. La difesa della natura è redditizia per le nazioni."
      ],
      [
        "Antoine de Saint-Exupéry",
        "Non ereditiamo la terra dai nostri genitori, la prendiamo in prestito dai nostri figli."
      ],
      [
        "Charles F. Kettering",
        "Il mondo odia il cambiamento, eppure è l’unica cosa che gli ha permesso di progredire."
      ],
      [
        "Peter Drucker",
        "Non c’è sicuramente nulla di così inutile come fare molto efficacemente ciò che non dovrebbe essere fatto affatto."
      ],
      [
        "Martin Luther King",
        "Se mi dicessero che la fine del mondo è per domani, pianterei comunque un melo."
      ],
      [
        "Kenneth Boulding",
        "Chi crede che la crescita possa essere infinita in un mondo finito è o un pazzo o un economista."
      ],
      [
        "Victor Hugo",
        "È una cosa triste pensare che la natura parla e che il genere umano non l’ascolta."
      ],
      [
        "Marco Aurelio",
        "Segui sempre la strada più breve, e la più breve è la strada tracciata dalla natura."
      ],
      [
        "Confucio",
        "Chi sposta una montagna inizia spostando piccole pietre."
      ],
      [
        "Henry Ford",
        "Non cercate la colpa, cercate il rimedio."
      ],
      [
        "Sant’Agostino",
        "È meglio seguire la strada giusta zoppicando che quella sbagliata con passo fermo."
      ]
    ],
    "stop_charge": "Interrompere la ricarica",
    "last_power": "Potenza misurata",
    "charge_is_starting": "La colonnina si sta avviando.\nCi potrebbe mettere fino a 30 secondi.",
    "already_consumed": "Energia ricaricata",
    "already_occupied": "Terminale occupato da",
    "total": "Energia erogata a",
    "total_guest": "Energia erogata",
    "confirm_stop_charge": "Sei sicuro di voler interrompere la ricarica?",
    "suspended": "Questo punto di ricarica è temporaneamente in stato di sospensione: la stazione di ricarica non dispone di energia sufficiente per caricare tutte le colonnine."
  },
  "fr": {
    "quotes": [
      [
        "Pierre Rabhi, Le Temps de la simplicité",
        "L’économie doit être au service de la vie et non pas de la destruction de l'environnement."
      ],
      [
        "Pierre Rabhi, Vers la sobriété heureuse",
        "Changer notre façon de produire et de consommer, c'est l'alpha et l'oméga de l’écologie intégrale."
      ],
      [
        "Pierre Rabhi, La Part du Colibri",
        "Chaque acte quotidien peut être un acte de résistance face à la destruction de la planète."
      ],
      [
        "Pierre Rabhi, Le Temps de la simplicité",
        "Si nous voulons un avenir, il nous faut penser différemment notre rapport au monde et aux autres."
      ],
      [
        "Pierre Rabhi, Le Rabhi des champs",
        "L’humilité est la clef pour rétablir l’harmonie entre l’homme et la nature. Nous sommes à l'écoute de la Terre."
      ],
      [
        "Pierre Rabhi, Vers la sobriété heureuse",
        "La Terre est une maison commune à préserver. L'agriculture doit retrouver son âme, nourrir l'humain et respecter la biodiversité."
      ],
      [
        "Pierre Rabhi, Le Temps de la simplicité",
        "L'écologie est avant tout une question de respect de la vie sous toutes ses formes."
      ],
      [
        "Pierre Rabhi, Le Rabhi des champs",
        "Nous ne pouvons plus ignorer les souffrances de la Terre. Il est urgent de changer notre rapport à la nature."
      ],
      [
        "Pierre Rabhi, Vers la sobriété heureuse",
        "L'écologie intégrale, c'est une vision globale où l'humain, la nature et les sociétés sont dans un équilibre durable."
      ],
      [
        "Pierre Rabhi, La Part du Colibri",
        "Il n'y a pas de justice sociale sans justice écologique, car l’un est la condition de l’autre."
      ],
      [
        "Jean-Marc Jancovici, Le piège de la démesure",
        "La transition énergétique n’est pas un choix, c’est une nécessité absolue si nous voulons éviter le chaos climatique et écologique."
      ],
      [
        "Jean-Marc Jancovici, L'Enfer climatique est pavé de bonnes intentions",
        "L’énergie est le moteur de tout, mais notre modèle économique l’utilise sans limite, mettant en péril l’équilibre planétaire."
      ],
      [
        "Jean-Marc Jancovici, L'Enfer climatique est pavé de bonnes intentions",
        "Notre mode de vie est insoutenable car il repose sur une consommation d’énergie fossile qui déstabilise l’ensemble du système Terre."
      ],
      [
        "Jean-Marc Jancovici, Le piège de la démesure",
        "Les enjeux écologiques doivent être considérés comme un tout : tout est lié, de l’énergie à la biodiversité."
      ],
      [
        "Jean-Marc Jancovici, L'Enfer climatique est pavé de bonnes intentions",
        "Il faut penser autrement, en adoptant une vision systémique de l’écologie, où chaque action a des conséquences à grande échelle."
      ],
      [
        "Jean-Marc Jancovici, Le piège de la démesure",
        "L'humain doit impérativement revoir ses comportements, car la crise écologique est d'abord une crise de notre relation à la Terre."
      ],
      [
        "Jean-Marc Jancovici, L'Enfer climatique est pavé de bonnes intentions",
        "La transition écologique exige un changement de modèle économique : la croissance infinie est incompatible avec les limites de la planète."
      ],
      [
        "Jean-Marc Jancovici, Le piège de la démesure",
        "Notre défi est d’arrêter de dilapider les ressources naturelles comme si elles étaient inépuisables. Le respect de la Terre doit guider nos choix."
      ],
      [
        "Jean-Marc Jancovici, L'Enfer climatique est pavé de bonnes intentions",
        "Nous avons encore le pouvoir de choisir nos actions, mais ce pouvoir est conditionné par notre capacité à comprendre les enjeux écologiques."
      ],
      [
        "Jean-Marc Jancovici, Le piège de la démesure",
        "L'écologie intégrale, c'est la reconnaissance de l’interdépendance des systèmes naturels et humains. Le respect des uns est le respect des autres."
      ],
      [
        "Ivan Illich, La convivialité",
        "La technologie moderne ne libère pas l'homme ; elle le rend dépendant et détruit ses capacités à vivre simplement et harmonieusement."
      ],
      [
        "Ivan Illich, Énergie et équité",
        "L’écologie véritable n’est pas celle des experts, mais celle qui respecte l'autonomie des communautés et leur relation à la nature."
      ],
      [
        "Ivan Illich, La convivialité",
        "La destruction de l'environnement commence lorsque l'homme se croit supérieur à la nature, oubliant qu'il fait partie d'elle."
      ],
      [
        "Ivan Illich, La convivialité",
        "La société industrielle crée une division entre l'homme et la nature, il est urgent de rétablir une relation authentique avec le monde vivant."
      ],
      [
        "Ivan Illich, Énergie et équité",
        "La vraie liberté vient de la capacité à se reconnecter à la nature et à vivre de manière simple, sans la surconsommation imposée."
      ],
      [
        "Ivan Illich, La convivialité",
        "L'homme moderne a perdu l'art de vivre avec la nature. L’écologie intégrale doit reconstruire ce lien fondamental."
      ],
      [
        "Ivan Illich, Énergie et équité",
        "Le progrès ne se mesure pas à la quantité de biens produits, mais à la capacité des sociétés à créer des formes de vie soutenables."
      ],
      [
        "Ivan Illich, La convivialité",
        "Pour rétablir l'équilibre écologique, il faut réinventer des formes de vie qui respectent les limites de la planète et les besoins humains."
      ],
      [
        "Ivan Illich, Énergie et équité",
        "Une société qui cherche à croître indéfiniment est vouée à l’effondrement, car elle oublie les contraintes écologiques qui régissent la Terre."
      ],
      [
        "Ivan Illich, La convivialité",
        "Nous devons revenir à une éthique de simplicité, en privilégiant des modes de vie qui respectent les cycles naturels et les besoins essentiels."
      ],
      [
        "Hartmut Rosa, Accélération. Une critique sociale du temps",
        "Le progrès infiniment croissant mène à un épuisement des ressources et à un déclin du lien entre l’homme et la nature."
      ],
      [
        "Hartmut Rosa, Accélération. Une critique sociale du temps",
        "Une société qui court toujours plus vite détruit son rapport au monde et aux autres, et par là, son environnement."
      ],
      [
        "Hartmut Rosa, Accélération. Une critique sociale du temps",
        "L’accélération du monde engendre des inégalités sociales et un épuisement des ressources, deux phénomènes indissociables de notre mode de vie."
      ],
      [
        "Hartmut Rosa, L’éthique de l’accélération",
        "L’écologie intégrale suppose de réinventer un rapport respectueux à la nature, loin de la logique de consommation sans fin."
      ],
      [
        "Hartmut Rosa, Accélération. Une critique sociale du temps",
        "La quête infinie de vitesse et de progrès est incompatible avec la préservation d’un monde naturel vivant et divers."
      ],
      [
        "Hartmut Rosa, Accélération. Une critique sociale du temps",
        "Le véritable changement écologique ne passera pas par l’innovation technologique, mais par une transformation de notre rapport au temps et au monde."
      ],
      [
        "Hartmut Rosa, Accélération. Une critique sociale du temps",
        "Le temps industriel, qui impose de produire toujours plus vite, détruit les rythmes naturels et les liens sociaux essentiels à notre bien-être."
      ],
      [
        "Hartmut Rosa, L’éthique de l’accélération",
        "L’éco-modernisme, qui propose une solution technologique à la crise écologique, ne fait qu'aggraver l’aliénation et l’épuisement des ressources."
      ],
      [
        "Hartmut Rosa, Accélération. Une critique sociale du temps",
        "La durabilité n'est pas seulement une question de ressources, mais aussi une question de rythme : celui de la nature et de l’humain."
      ],
      [
        "Hartmut Rosa, L’éthique de l’accélération",
        "Pour une écologie intégrale, il est essentiel de ralentir le rythme de vie, afin de renouer avec le monde vivant dans toute sa complexité."
      ],
      [
        "Corinne Peluchon, Pour une écologie du vivant",
        "L'écologie intégrale est un appel à rétablir un équilibre entre l’humain et la nature, respectant les droits des autres êtres vivants."
      ],
      [
        "Corinne Peluchon, L'éthique de la considération",
        "L’harmonie entre l’homme et la nature nécessite une transformation radicale de notre mode de vie et de nos valeurs."
      ],
      [
        "Corinne Peluchon, Pour une écologie du vivant",
        "La justice écologique n'est pas seulement une question environnementale, elle touche à la dignité et aux droits de tous les êtres vivants."
      ],
      [
        "Corinne Peluchon, L'éthique de la considération",
        "Nous devons repenser notre rapport à la nature, non plus comme une ressource à exploiter, mais comme un partenaire à respecter."
      ],
      [
        "Corinne Peluchon, Pour une écologie du vivant",
        "Une société juste est une société où la nature, les autres êtres vivants et les générations futures sont pris en compte dans nos choix."
      ],
      [
        "Corinne Peluchon, L'éthique de la considération",
        "L’écologie intégrale appelle à une transformation des mentalités : il ne s'agit pas de sauver la planète, mais de repenser l’humanité."
      ],
      [
        "Corinne Peluchon, Pour une écologie du vivant",
        "Nous devons intégrer la nature dans nos systèmes de pensée, non comme un simple décor, mais comme un acteur essentiel de notre vie."
      ],
      [
        "Corinne Peluchon, L'éthique de la considération",
        "La prise en compte de la souffrance animale est au cœur de l'écologie intégrale, car elle dépasse l’écosystème pour inclure tous les vivants."
      ],
      [
        "Corinne Peluchon, Pour une écologie du vivant",
        "Les choix politiques en matière écologique doivent prendre en compte l’interconnexion des écosystèmes et la dignité de tous les êtres vivants."
      ],
      [
        "Corinne Peluchon, L'éthique de la considération",
        "L'écologie intégrale nous invite à vivre en harmonie avec les cycles naturels, en respectant les limites de notre planète et la vie qu'elle porte."
      ],
      [
        "Philippe Pierron, La nature et ses désastres",
        "L’écologie intégrale nous pousse à repenser la place de l’homme dans l’univers, à la fois vulnérable et responsable envers la Terre."
      ],
      [
        "Philippe Pierron, La nature et ses désastres",
        "Nous ne pouvons plus continuer à ignorer l’interconnexion des systèmes naturels et humains, car leur équilibre détermine notre avenir commun."
      ],
      [
        "Philippe Pierron, La nature et ses désastres",
        "La véritable crise écologique est d’abord une crise de relation entre l’homme et la nature, où l’indifférence domine."
      ],
      [
        "Philippe Pierron, La nature et ses désastres",
        "Penser l'écologie intégrale, c’est reconnaître que l’homme et la nature ne sont pas séparés mais forment un tout indissociable."
      ],
      [
        "Philippe Pierron, La nature et ses désastres",
        "Le respect de la nature passe par une transformation intérieure, un changement de regard sur ce qui nous entoure."
      ],
      [
        "Philippe Pierron, La nature et ses désastres",
        "Une écologie authentique ne se contente pas de sauver la planète, mais cherche à restaurer une relation de réciprocité avec elle."
      ],
      [
        "Philippe Pierron, La nature et ses désastres",
        "La crise écologique est aussi une crise spirituelle : nous avons perdu le sens du sacré dans notre relation au monde naturel."
      ],
      [
        "Philippe Pierron, La nature et ses désastres",
        "L’écologie doit dépasser la simple gestion des ressources pour inclure une réflexion éthique sur notre responsabilité envers les autres vivants."
      ],
      [
        "Philippe Pierron, La nature et ses désastres",
        "L’écologie intégrale nous invite à réconcilier la dimension matérielle et spirituelle de l’homme, pour vivre en harmonie avec le monde vivant."
      ],
      [
        "Philippe Pierron, La nature et ses désastres",
        "La terre ne nous appartient pas : nous faisons partie d’un écosystème qu’il nous faut préserver pour les générations futures."
      ]
    ],
    "stop_charge": "Arrêter la charge",
    "last_power": "Puissance",
    "charge_is_starting": "La borne de recharge est sous tension.\nCela peut prendre jusqu'à 30 secondes",
    "already_consumed": "Énergie consomméè",
    "already_occupied": "borne occupée par",
    "total": "Énergie fournie à",
    "total_guest": "Énergie fournie",
    "confirm_stop_charge": "Êtes-vous sûr de vouloir arrêter la charge ?",
    "suspended": "Cette borne de recharge est actuellement suspendue : il n'y a pas assez de puissance pour alimenter toutes les bornes de recharge."
  },
  "de": {
    "quotes": [
      [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ],
      [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ],
      [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ],
      [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ],
      [ "", "" ]
    ],
    "stop_charge": "Aufhören zu laden",
    "last_power": "Gemessene Leistung",
    "charge_is_starting": "Die Ladestation wird eingeschaltet.\nEs kann bis zu 30 Sekunden dauern",
    "already_consumed": "Energia ricaricata",
    "already_occupied": "Terminal besetzt von",
    "total": "Energie abgegeben zu",
    "total_guest": "Energie abgegeben",
    "confirm_stop_charge": "Sind Sie sicher, dass Sie den Ladevorgang beenden?",
    "suspended": "Diese Ladestation ist derzeit gesperrt: Es ist nicht genügend Strom vorhanden, um alle Ladestationen zu versorgen."
  },
  "nl": {
    "quotes": [
      [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ],
      [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ],
      [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ],
      [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ], [ "", "" ],
      [ "", "" ]
    ],
    "stop_charge": "Opladen stoppen",
    "last_power": "Vermogen",
    "charge_is_starting": "Het laadpunt wordt ingeschakeld.\nHet kan maximaal 30 seconden duren",
    "already_consumed": "Verbruikte energie",
    "already_occupied": "Laadpunt bezet door",
    "total": "Totaal verbruik voor",
    "total_guest": "Totaal verbruik",
    "confirm_stop_charge": "Weet u zeker dat u wilt stoppen met opladen?",
    "suspended": "Diese Ladestation ist derzeit gesperrt: Es ist nicht genügend Strom vorhanden, um alle Ladestationen zu versorgen."
    }
}
</i18n>

<style scoped lang="scss">
</style>

<!-- vim: softtabstop=2:shiftwidth=2
-->
